import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import Packages from "@/components/Packages";
import LeadForm from "@/components/LeadForm";
import SampleAdsCTASection from "@/components/SampleAdsCTASection";
import { tradeList } from "@/data/trades";
import {
  Snowflake,
  Wrench,
  Zap,
  Home as HomeIcon,
  Leaf,
  Bug,
  ArrowRight,
  CloudLightning,
  Target,
  Crosshair,
  FileBadge,
  Wand2,
  Quote,
} from "lucide-react";

const ICONS = { Snowflake, Wrench, Zap, Home: HomeIcon, Leaf, Bug };

const HomeServicesHub = () => {
  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="eyebrow mb-6">
                For Home Services Pros · Charlottesville, VA
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7">
                Your{" "}
                <span className="text-[var(--af-lime)]">first 90 days</span>{" "}
                with us. Then we go from there.
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-8 leading-relaxed">
                A proven 90-day playbook for Central Virginia HVAC, plumbing,
                electrical, roofing, lawn & landscape, and other home services
                pros. We take the guesswork out of your ad buy and lead with
                the campaigns we've already seen book busy seasons in your
                trade. Three months in, you'll see the calls, the bookings,
                and the data, and we'll extend the relationship from a place
                of evidence. All starter packages under $10K.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#trades"
                  className="btn-lime"
                  data-testid="hero-cta-pick-trade"
                >
                  Pick Your Trade <ArrowRight size={14} />
                </a>
                <a
                  href="#lead-form"
                  className="btn-ghost"
                  data-testid="hero-cta-audit"
                >
                  Free Market Audit
                </a>
              </div>
            </div>

            {/* Big stat block */}
            <div className="lg:col-span-5">
              <div
                className="surface p-7 fade-up"
                style={{ animationDelay: ".15s" }}
              >
                <div className="eyebrow mb-3">Our Time-Tested Campaigns</div>
                <div className="font-display text-4xl sm:text-5xl leading-tight mt-2">
                  Have a seasonal campaign{" "}
                  <span className="text-[var(--af-lime)]">
                    you&apos;d like to run?
                  </span>
                </div>
                <p className="text-base mt-5 text-[var(--af-cream)]/75 leading-relaxed">
                  One of these packages will be the perfect way to get your
                  message out!
                </p>
                <div className="mt-6 pt-5 border-t border-[var(--af-line)]">
                  <a
                    href="#packages"
                    className="text-xs font-semibold uppercase tracking-widest text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-1.5"
                    data-testid="hs-hero-packages-cta"
                  >
                    See the packages <ArrowRight size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE — Trade Grid */}
      <section
        id="trades"
        className="relative px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="trades-section"
      >
        <div className="mb-14 max-w-3xl">
          <div className="eyebrow mb-4">Who We Serve</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
            Pick your trade.
            <br />
            <span className="text-[var(--af-lime)]">
              We'll show you how
            </span>{" "}
            to win it.
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl">
            Every trade has its own season, its own buyer, and its own
            triggers. Click yours below for messaging, examples, and a
            tailored plan.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {tradeList.map((t) => {
            const Icon = ICONS[t.icon] || HomeIcon;
            return (
              <Link
                key={t.slug}
                to={`/home-services/${t.slug}`}
                className="surface p-6 flex flex-col group"
                data-testid={`trade-card-${t.slug}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center">
                    <Icon size={22} className="text-[var(--af-lime)]" />
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-[var(--af-cream)]/40 group-hover:text-[var(--af-lime)] group-hover:translate-x-1 transition-all"
                  />
                </div>
                <div className="eyebrow mb-2">Home Services</div>
                <div className="font-display text-2xl mb-2">{t.name}</div>
                <p className="text-sm text-[var(--af-cream)]/70 mb-5 flex-1 leading-relaxed">
                  {t.heroSub.split(".")[0]}.
                </p>
                <div className="text-sm font-semibold text-[var(--af-lime)] group-hover:underline underline-offset-4">
                  See the {t.shortName} playbook →
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* DASHED DIVIDER */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="dashed-divider" />
      </div>

      {/* PACKAGES */}
      <Packages />

      {/* CLIENT TESTIMONIAL — Gardner Lloyd, Charlottesville Remodeling Company */}
      <section
        className="grid-bg border-t border-b border-[var(--af-line)] px-5 sm:px-8 py-24"
        data-testid="hs-hub-testimonial"
      >
        <div className="max-w-4xl mx-auto text-center">
          <Quote size={42} className="text-[var(--af-lime)] mx-auto mb-6" />
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
            &ldquo;The Anne Flinn Advertising agency has been wonderful to work
            with. They have revamped our company&apos;s marketing strategy with
            a more innovative, targeted, and personal process. They provide
            regular feedback allowing us to incorporate a more meaningful and
            effective marketing strategy for our company. As an added bonus to
            their marketing acumen&nbsp;&mdash; Anne, Collean, and the other
            members of the team are delightful individuals. Absolutely
            fantastic to work with. I could not recommend them more!&rdquo;
          </p>
          <div className="eyebrow justify-center inline-flex">
            Gardner Lloyd &middot; Charlottesville Remodeling Company
          </div>
        </div>
      </section>

      <SampleAdsCTASection
        label="Home Services"
        audience="home-services"
        testIdPrefix="hs-hub-sample-ads"
      />

      {/* HOW WE'RE DIFFERENT */}
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="differentiators-section"
      >
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Why Home Services Pros Pick Us</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5">
              Every campaign is built for one outcome:
              <br />
              <span className="text-[var(--af-lime)]">
                filled calendars.
              </span>
            </h2>
            <p className="text-base text-[var(--af-cream)]/75 leading-relaxed">
              A boutique agency means you work directly with Anne. The
              principal owns the strategy and the creative every week, with a
              plan that respects your season, your service area, and your
              margins.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: CloudLightning,
                t: "Weather-triggered ads",
                d: "Heat wave? Freeze? Hailstorm? Your creative activates within hours, automatically.",
              },
              {
                icon: Crosshair,
                t: "Hyper-local targeting",
                d: "Zip codes, custom radius, competitor service areas — reach the right neighborhoods with PMAX precision.",
              },
              {
                icon: FileBadge,
                t: "Permit + new-mover lists",
                d: "Target households actively renovating, moving in, or pulling permits in your service area.",
              },
              {
                icon: Wand2,
                t: "Creative built in-house",
                d: "Concept, copy, photo direction, video, and motion design, all done by our team. You get scroll-stopping ads that actually sound like your brand, not a templated stock spot.",
              },
            ].map((d, i) => (
              <div
                key={i}
                className="surface p-6"
                data-testid={`differentiator-${i}`}
              >
                <d.icon
                  size={22}
                  className="text-[var(--af-lime)] mb-3"
                />
                <div className="font-display text-xl mb-2">{d.t}</div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {d.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="grid-bg px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">Take the First Step</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              20 minutes.
              <br />
              A real plan.
              <br />
              <span className="text-[var(--af-lime)]">No sales theater.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Tell us your trade and your goal. We'll come back with which
              neighborhoods to target, what offer to lead with, and what your
              90-day numbers should look like.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute market audit
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Custom playbook for your trade
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                No obligation, no spam
              </li>
            </ul>
          </div>
          <LeadForm trade="general" tradeName="Home Services" />
        </div>
      </section>
    </SiteShell>
  );
};

export default HomeServicesHub;
